import { faker } from '@faker-js/faker';

import type { DonutDatum } from '@/api/types';

/** Provider health state — drives the provider-status card's colored indicator. */
export type ProviderHealth = 'operational' | 'degraded' | 'down';

export const PROVIDER_HEALTH_STATES: readonly ProviderHealth[] = [
	'operational',
	'degraded',
	'down',
];

/** One config toggle on a provider card (the toggle-driven form). */
export interface ProviderToggle {
	id: string;
	label: string;
	enabled: boolean;
}

/** One provider-status card. */
export interface ProviderCard {
	id: string;
	name: string;
	health: ProviderHealth;
	/** Whether the provider is enabled in the routing pool. */
	enabled: boolean;
	/** Share of total send volume routed to this provider (0-100). */
	usagePct: number;
	region: string;
	toggles: ProviderToggle[];
}

/** Settings page payload: provider cards + a usage-split donut. */
export interface SettingsData {
	providers: ProviderCard[];
	/** Provider usage split — donut chart. */
	usageSplit: DonutDatum[];
}

const PROVIDER_NAMES = ['Amazon SES', 'SendGrid', 'Postmark', 'Mailgun'] as const;

function makeToggles(): ProviderToggle[] {
	return [
		{ id: 'tracking', label: 'Open & click tracking', enabled: faker.datatype.boolean() },
		{ id: 'sandbox', label: 'Sandbox mode', enabled: faker.datatype.boolean() },
		{ id: 'retry', label: 'Auto-retry on failure', enabled: faker.datatype.boolean({ probability: 0.8 }) },
	];
}

export function getSettingsMock(): SettingsData {
	const rawShares = PROVIDER_NAMES.map(() => faker.number.int({ min: 10, max: 100 }));
	const shareTotal = rawShares.reduce((sum, n) => sum + n, 0);

	const providers: ProviderCard[] = PROVIDER_NAMES.map((name, i) => ({
		id: faker.string.uuid(),
		name,
		health: faker.helpers.weightedArrayElement([
			{ weight: 75, value: 'operational' },
			{ weight: 18, value: 'degraded' },
			{ weight: 7, value: 'down' },
		]),
		enabled: i === 0 ? true : faker.datatype.boolean({ probability: 0.7 }),
		usagePct: Number(((rawShares[i] / shareTotal) * 100).toFixed(1)),
		region: faker.helpers.arrayElement(['us-east-1', 'us-west-2', 'eu-west-1']),
		toggles: makeToggles(),
	}));

	const usageSplit: DonutDatum[] = providers.map((p) => ({ name: p.name, value: p.usagePct }));

	return { providers, usageSplit };
}
